import { setUserData, setTransCode } from '@api/user';
import { ColumnWrap, Column } from '@components/shared/View';
import AlertModal from '@components/shared/modals/AlertModal';
import { STATUS_CODE } from '@constants/api';
import { FONT_SIZE, PADDING, PASSWORD_MAX_LENGTH } from '@constants/numbers';
import { IS_TRANS_CODE_ENABLED, IS_EMBEDDED_APP } from '@constants/config';
// import { SWITCH_ROUTES } from '@constants/routes';
import { useGlobalProps } from '@hooks/useGlobalProps';
import { parseSetUerDataRes } from '@utils/parseErrors';
import { validPassword } from '@utils/verify';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import UserProfileSection from '@components/profile/UserProfileSection';

const COLUMNS = [
    { title: 'old-password', name: 'passowrd' },
    { title: 'new-password', name: 'newPassowrd' },
    { title: 'confirm-password', name: 'confirmPassowrd' },
];
const INIT_ERROR = {
    passowrd: '',
    newPassowrd: '',
    confirmPassowrd: '',
};
const INIT_TRANS_CODE = {
    otc: '',
    ntc: '',
    confirmNtc: '',
};
const INIT_ALERT = {
    show: false,
    title: '',
};
export default function PasswordPage() {
    const [info, setInfo] = useState({ ...INIT_ERROR });
    const [error, setError] = useState({ ...INIT_ERROR });
    const [alert, setAlert] = useState({ ...INIT_ALERT });
    const [transCodeInfo, setTransCodeInfo] = useState({ ...INIT_TRANS_CODE });
    const [transCodeError, setTransCodeError] = useState({ ...INIT_TRANS_CODE });
    const { t } = useTranslation();
    const { user } = useGlobalProps();
    const tUser = t('user', { returnObjects: true });
    const tCommon = t('common', { returnObjects: true });
    const tModal = t('modal', { returnObjects: true });
    const tError = t('error', { returnObjects: true });
    const tTransCode = t('transCode', { returnObjects: true });

    // tc_mode: 0=not set, 1=set (readonly), 2=reset needed, 6=locked, 9=ignore
    const tcMode = user?.tc_mode;
    const showTransCodeSection = IS_TRANS_CODE_ENABLED && tcMode !== 9 && tcMode !== undefined;

    const handleInputChange = (e) => {
        const target = e.target;
        const name = target.name;
        setInfo((prev) => {
            return { ...prev, [name]: target.value };
        });
    };

    const handleTransCodeChange = (e) => {
        const target = e.target;
        const name = target.name;
        setTransCodeInfo((prev) => {
            return { ...prev, [name]: target.value };
        });
    };

    const submitTransCode = async () => {
        const { otc, ntc, confirmNtc } = transCodeInfo;
        setTransCodeError({ ...INIT_TRANS_CODE });

        // Validate new trans code
        if (!ntc) {
            setTransCodeError({ ...INIT_TRANS_CODE, ntc: tTransCode['transCode@required'] });
            return;
        }

        // Validate confirmation matches
        if (ntc !== confirmNtc) {
            setTransCodeError({ ...INIT_TRANS_CODE, confirmNtc: tTransCode['transCode@mismatch'] });
            return;
        }

        // For tc_mode=2, require old trans code
        if (tcMode === 2 && !otc) {
            setTransCodeError({ ...INIT_TRANS_CODE, otc: tTransCode['transCode@required'] });
            return;
        }

        const req = { ukey: user.ukey, ntc };
        if (tcMode === 2) {
            req.otc = otc;
        }

        const res = await setTransCode(req);
        if (res?.rtn === STATUS_CODE.SUCCESS) {
            setAlert({
                show: true,
                title: tCommon['common@success'],
                message: tTransCode['transCode@setSuccess'],
            });
            setTransCodeInfo({ ...INIT_TRANS_CODE });
            // Refresh user info to update tc_mode
            window.location.reload();
        } else {
            setAlert({
                show: true,
                title: tCommon['common@failed'],
                message: res?.msg || tError['error@server_error'],
            });
        }
    };

    const renderTransCodeSection = () => {
        if (!showTransCodeSection) return null;

        // tc_mode=6: Locked
        if (tcMode === 6) {
            return (
                <TransCodeSection>
                    <h3>{tTransCode['transCode@title']}</h3>
                    <div className="locked-message">{tTransCode['transCode@locked']}</div>
                </TransCodeSection>
            );
        }

        // tc_mode=1: Already set (show masked)
        if (tcMode === 1) {
            return (
                <TransCodeSection>
                    <h3>{tTransCode['transCode@title']}</h3>
                    <div className="trans-code-display">
                        <span className="label">{tTransCode['transCode@title']}:</span>
                        <span className="masked">{tTransCode['transCode@masked']}</span>
                    </div>
                </TransCodeSection>
            );
        }

        // tc_mode=0: Not set yet
        // tc_mode=2: Reset needed (requires old trans code)
        return (
            <TransCodeSection>
                <h3>{tTransCode['transCode@title']}</h3>
                <ColumnWrap>
                    {tcMode === 2 && (
                        <Column
                            title={tTransCode['transCode@current']}
                            name="otc"
                            value={transCodeInfo.otc}
                            errorMsg={transCodeError.otc}
                            onChange={handleTransCodeChange}
                            maxLength={PASSWORD_MAX_LENGTH}
                        />
                    )}
                    <Column
                        title={tTransCode['transCode@new']}
                        name="ntc"
                        value={transCodeInfo.ntc}
                        errorMsg={transCodeError.ntc}
                        onChange={handleTransCodeChange}
                        maxLength={PASSWORD_MAX_LENGTH}
                    />
                    <Column
                        title={tTransCode['transCode@confirm']}
                        name="confirmNtc"
                        value={transCodeInfo.confirmNtc}
                        errorMsg={transCodeError.confirmNtc}
                        onChange={handleTransCodeChange}
                        maxLength={PASSWORD_MAX_LENGTH}
                    />
                </ColumnWrap>
                <div className="trans-code-button-wrap">
                    <button onClick={submitTransCode}>{tTransCode['transCode@set']}</button>
                </div>
            </TransCodeSection>
        );
    };
    // const openLiveChat = () => {
    //     window.open(SWITCH_ROUTES.LIVE_CHAT)
    // }

    const submit = async () => {
        const { passowrd, newPassowrd, confirmPassowrd } = info;
        setError({ ...INIT_ERROR });
        if (!newPassowrd || !validPassword(newPassowrd)) {
            setError({ ...INIT_ERROR, newPassowrd: tUser['hint@password'] });
            return;
        }
        if (newPassowrd !== confirmPassowrd) {
            setError({
                ...INIT_ERROR,
                confirmPassowrd: tUser['hint@confirm-password'],
            });
            return;
        }
        const res = await setUserData({
            ukey: user.ukey,
            opwd: passowrd,
            npwd: newPassowrd,
        });
        if (res?.rtn === STATUS_CODE.SUCCESS) {
            setAlert({
                show: true,
                title: tCommon['common@success'],
                message: tModal['password@modify-successful'],
            });
            setInfo({ ...INIT_ERROR });
        } else if (res.rtn) {
            setAlert({
                show: true,
                title: tCommon['common@failed'],
                message: parseSetUerDataRes(tError, res),
            });
        } else {
            setAlert({
                show: true,
                title: tCommon['common@failed'],
                message: parseSetUerDataRes(tError, res),
            });
        }
    };

    return (
        <>
            <PasswordPageWrap>
                <UserProfileSection />
                {!IS_EMBEDDED_APP && (
                    <>
                        <ColumnWrap className="custom-column-wrap">
                            {COLUMNS.map((c) => (
                                <Column
                                    key={c.title}
                                    title={tUser[`user@${c.title}`]}
                                    name={c.name}
                                    value={info[c.name]}
                                    errorMsg={error[c.name]}
                                    onChange={handleInputChange}
                                    maxLength={PASSWORD_MAX_LENGTH}
                                />
                            ))}
                        </ColumnWrap>
                        <div className="password-button-wrap">
                            <button onClick={submit}>{tCommon['common@submit']}</button>
                        </div>
                    </>
                )}
                {renderTransCodeSection()}
                <AlertModal
                    {...alert}
                    onConfirm={() => setAlert({ ...INIT_ALERT })}
                />
            </PasswordPageWrap>
        </>
    );
}

const PasswordPageWrap = styled.div`
    width: 100%;
    box-sizing: border-box;
    font-size: ${FONT_SIZE.NORMAL};
    padding: ${PADDING.USER_INFO_PAGE};
    .custom-column-wrap {
        margin-top: 20px; /* or however much space you prefer */
    }
    .password-button-wrap {
        display: flex;
        flex-direction: row;
        gap: 20px;
        margin-top: 20px;
        padding-bottom: 5px;

        button {
            color: white !important;
        }
        button:first-child {
            width: 90px;
            background: ${({ theme }) => theme.primary};
        }
        button:nth-child(2) {
            color: black !important;
            border: ${({ theme }) => `2px solid ${theme.primary}`};
        }
    }
`;

const TransCodeSection = styled.div`
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #eee;

    h3 {
        margin: 0 0 15px 0;
        font-size: ${FONT_SIZE.NORMAL};
        color: ${({ theme }) => theme.primary};
    }

    .locked-message {
        color: #d9534f;
        padding: 10px;
        background: #fdf2f2;
        border-radius: 4px;
    }

    .trans-code-display {
        display: flex;
        gap: 10px;
        align-items: center;

        .label {
            color: #666;
        }

        .masked {
            font-weight: bold;
            letter-spacing: 2px;
        }
    }

    .trans-code-button-wrap {
        display: flex;
        margin-top: 15px;

        button {
            width: 90px;
            color: white !important;
            background: ${({ theme }) => theme.primary};
        }
    }
`;
